"use client";

import React from "react";
import { Bell } from "lucide-react";

import { Switch } from "@/components/ui/switch";
import { Paragraph } from "@/components/ui/typography";
import { toast } from "@/components/ui/sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import { subscribeToClassNotification } from "@/fetchers/live-class";

import { cn } from "@/lib/utils";

function LiveClassNotification({ liveClass, onChange }) {
  const {
    courseId,
    isNotificationEnabled,
    freeLiveClass: isFreeLiveClass,
  } = liveClass;

  const isAllowed =
    liveClass.isNotificationAllowed &&
    liveClass.status === 1 &&
    liveClass.startTime > new Date().getTime() &&
    liveClass.freeLiveClass !== true;

  const [isModelOpen, setIsModelOpen] = React.useState(false);
  const [isSubscribed, setIsSubscribed] = React.useState(isNotificationEnabled);

  if (!isAllowed) return null;

  const handleOnChangeOfCourseNotification = async (checked) => {
    setIsSubscribed(checked);

    const response = await subscribeToClassNotification({
      courseId,
      subscribe: checked,
    });

    if (!response.data) {
      toast({
        variant: "error",
        description: `Unable to ${checked ? "Subscribe" : "Un-Subscribe"} to Notifications!`,
      });

      setIsSubscribed(!checked);
      return;
    }

    toast({
      variant: "success",
      description: `${checked ? "Subscribed" : "Un-Subscribed"} to Notifications!`,
    });

    if (typeof onChange === "function") {
      onChange(liveClass);
    }

    setIsModelOpen(false);
  };

  return (
    <>
      <div className="absolute inset-0 pointer-events-none rounded-md">
        <div className="absolute inset-0 overflow-hidden rounded-md">
          <button
            type="button"
            className={cn(
              "absolute -top-1.25 -left-1.25 inline-flex justify-center items-center size-8",
              "rounded-full bg-[#EBECFE] text-primary text-xl pointer-events-auto cursor-pointer",
              "transition-colors duration-300",
              {
                "bg-[#CFE4E1] border-[#CFE4E1]": isSubscribed,
              }
            )}
            onClick={() => setIsModelOpen(true)}
          >
            <Bell
              className={cn("size-3.5", {
                "fill-[#11766A] text-[#11766A]": isSubscribed,
              })}
            />
          </button>
        </div>

        <div
          className={cn(
            "absolute bottom-0 left-1/2 whitespace-nowrap flex items-center",
            "bg-[#D9F3F0] rounded-sm py-0.5 px-1 gap-1 -translate-x-1/2 translate-y-1/2",
            { hidden: !isSubscribed }
          )}
        >
          <Bell className="size-3 fill-[#11766A] text-[#11766A]" />
          <span className="text-xs">
            We’ll notify you{" "}
            {isFreeLiveClass
              ? "for the live classes of this course"
              : "when the class is live"}
          </span>
        </div>
      </div>

      <Dialog open={isModelOpen} onOpenChange={setIsModelOpen}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Set reminder</DialogTitle>
          </DialogHeader>

          <Paragraph variant="b4">
            <b>Note:</b> You will receive an email before 30 mins for every live
            class as a reminder, Also you will be notified once recording of
            live session is available! You can turn off this anytime, just by
            toggling!
          </Paragraph>

          <div className="flex flex-col gap-5 py-4 px-3 bg-[#eceefe] rounded-md">
            <div className="flex justify-between text-md font-medium">
              <span>
                Notify me about all the live classes which gonna happen in this
                course
              </span>

              <Switch
                checked={isSubscribed}
                onCheckedChange={handleOnChangeOfCourseNotification}
                className="bg-primary-foreground"
              />
            </div>
          </div>

          <Paragraph variant="b5">
            You can sync all Live Classes with calendar from StudyIQ android app
          </Paragraph>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default LiveClassNotification;
